// Clickable table-header cell that toggles a column's sort. Pairs
// with `useSortableTable` — pass it the same sortKey/dir/toggle.
//
// Visual states:
//   - inactive: column key not the current sort. Shows a faint ⇅
//   - active asc: shows ▲
//   - active desc: shows ▼
//
// Passes className through so callers can keep their existing
// alignment (`text-right`, `text-left`, custom padding).

import type { SortDir } from "../hooks/useSortableTable.js";

interface Props {
  /** Visible label, e.g. "Volume". */
  label: string;
  /** The accessor key registered in useSortableTable's accessors map. */
  sortKey: string;
  /** From useSortableTable. */
  currentSortKey: string | null;
  currentDir: SortDir;
  onSort: (key: string) => void;
  /** Extra className for the <th>; useful for `text-right`, `pb-2`, etc. */
  className?: string;
  /** Tooltip on hover. */
  title?: string;
}

export function SortHeader({
  label, sortKey, currentSortKey, currentDir, onSort, className, title,
}: Props) {
  const active = currentSortKey === sortKey;
  const indicator = !active ? "⇅" : currentDir === "asc" ? "▲" : "▼";
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={title ?? `Sort by ${label}`}
        className={`inline-flex items-center gap-1 hover:text-ink-900 transition-colors ${active ? "text-ink-900 font-semibold" : "text-ink-500"}`}
      >
        <span>{label}</span>
        <span className={`text-[9px] leading-none ${active ? "opacity-100" : "opacity-40"}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}
